import axios, { type AxiosError } from 'axios'
import { i18n, t, type TKey } from '@/i18n'
import { translateServerMessage } from '@/i18n/serverMessages'
import type { ApiErrorCode, ErrorEnvelope } from '@/types/api'

/** The one error shape every service call rejects with. */
export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly requestId?: string
  readonly retryAfter?: number

  constructor(init: { status: number; code: ApiErrorCode; message: string; requestId?: string; retryAfter?: number }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.requestId = init.requestId
    this.retryAfter = init.retryAfter
  }

  get isCancelled() {
    return this.code === 'CANCELLED'
  }
  get isForbidden() {
    return this.status === 403
  }
  get isNotFound() {
    return this.status === 404
  }
  get isConflict() {
    return this.status === 409
  }
  get isRetryable() {
    return this.status >= 500 || this.code === 'NETWORK' || this.code === 'TIMEOUT'
  }
}

const statusFallback: Record<number, ApiErrorCode> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  412: 'FAILED_PRECONDITION',
  413: 'PAYLOAD_TOO_LARGE',
  429: 'RATE_LIMITED',
  503: 'UNAVAILABLE',
}

/**
 * Localized text for codes whose server message is generic, used when the
 * server's own message has no translation. A specific message the backend
 * sends (validation, conflicts, "warehouse not found") is translated from the
 * catalog in src/i18n/server instead; see serverMessages.ts.
 */
const localizedCodes: Partial<Record<ApiErrorCode, TKey>> = {
  UNAUTHORIZED: 'errors.unauthorized',
  FORBIDDEN: 'errors.forbidden',
  NOT_FOUND: 'errors.notFound',
  RATE_LIMITED: 'errors.rateLimited',
  INTERNAL: 'errors.internal',
  UNAVAILABLE: 'errors.unavailable',
  NETWORK: 'errors.network',
  TIMEOUT: 'errors.timeout',
}
const fallback = (code: ApiErrorCode) => (localizedCodes[code] ? t(localizedCodes[code]) : undefined)

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isCancel(error)) {
    return new ApiError({ status: 0, code: 'CANCELLED', message: t('errors.cancelled') })
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorEnvelope>
    const response = axiosError.response
    if (!response) {
      const code: ApiErrorCode = axiosError.code === 'ECONNABORTED' ? 'TIMEOUT' : 'NETWORK'
      return new ApiError({ status: 0, code, message: fallback(code)! })
    }

    const envelope = response.data?.error
    const code = envelope?.code ?? statusFallback[response.status] ?? 'INTERNAL'
    const retryAfter = Number(response.headers?.['retry-after']) || undefined
    // A 5xx message is always the same "Internal Server Error": the generic text says it better.
    const translated = response.status < 500 && envelope?.message ? translateServerMessage(envelope.message, i18n.locale) : undefined
    return new ApiError({
      status: response.status,
      code,
      message: translated ?? fallback(code) ?? (envelope?.message || t('errors.failed')),
      requestId: response.headers?.['x-request-id'] as string | undefined,
      retryAfter,
    })
  }

  return new ApiError({
    status: 0,
    code: 'INTERNAL',
    message: error instanceof Error ? error.message : t('errors.unexpected'),
  })
}
